import React, { Dispatch, FunctionComponent, useMemo } from 'react';
import ColorPicker from '../../../../../common/components/colorPicker/ColorPicker';
import { IExteriorColour } from '../../../../../../../common/model/IColours';
import { Box, Typography } from '@material-ui/core';
import GenericAccordion from '../../../../../common/components/presentation/genericAccordion/GenericAccordion';
import IndicativeNumber from '../../../../../common/components/indicativeNumber/IndicativeNumber';
import SectionStatesWrapper from '../../../../../common/components/configuratorStates/SectionStatesWrapper';
import customiseExteriorStyles from './customiseExteriorStyles';
import Translation from '../../../../../common/components/translation/Translation';
import useWidgetContext from '../../../../../common/react/useWidgetContext';
import ColorAttributeDataLayerRest from '../../../../../common/utils/dataLayer/rest/colorAttributeDataLayerRest';

export interface ICustomiseExteriorProps {
  loading?: boolean;
  expanded: boolean;
  setExpanded: Dispatch<boolean>;
  disclaimer: boolean;
  onClick: (id: string) => void;
  choices: IExteriorColour[];
  activeChoice: string;
  error: Error;
  scrollToDisclaimer?: (e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => void;
}

const CustomiseExterior: FunctionComponent<ICustomiseExteriorProps> = ({
  expanded,
  setExpanded,
  disclaimer,
  onClick,
  choices,
  activeChoice,
  loading,
  error,
  scrollToDisclaimer,
}) => {
  const styles = customiseExteriorStyles();

  const {
    configuration: { dimensions },
  } = useWidgetContext();

  const activeColorTitle = useMemo(() => {
    const currentSelected = choices?.find((color: IExteriorColour) => activeChoice === color.id);

    return currentSelected?.title;
  }, [activeChoice]);

  const colorPickerHandler = (args: string) => {
    onClick(args);
    ColorAttributeDataLayerRest(args, choices, dimensions);
  };

  return (
    <GenericAccordion
      expanded={expanded}
      setExpanded={setExpanded}
      dataTest={'container:section:exterior'}
      summary={
        <>
          <Typography component={'h3'} variant={'sectionTitle'}>
            <Translation id={'spc.customise.customizeExterior.title'} />
            {disclaimer && (
              <IndicativeNumber
                dataTest="exteriorColor"
                displayNumber={<Translation id="spc.customise.indicative.exteriorColor" />}
                onClick={scrollToDisclaimer}
                className={styles.indicativeNumber}
              />
            )}
          </Typography>
        </>
      }
      details={
        <SectionStatesWrapper error={error} loading={loading || !choices?.length}>
          <Box display="flex" flexDirection="column">
            <Typography component={'span'} variant={'caption'} className={styles.activeColorTitle}>
              {activeColorTitle}
            </Typography>
            <ColorPicker onClick={colorPickerHandler} colorsArray={choices} activeColors={[activeChoice]} />
          </Box>
        </SectionStatesWrapper>
      }
    />
  );
};

export default CustomiseExterior;
